import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AumicoCoreProvider, IAumMenuOptionsForRow, ITableOptions, NotificationType } from '@modeso/aumico-lib-core-fe';
import { IUploadOrganizationReportingTemplateDto } from '@modeso/aumico-lib-users-fe/interfaces/upload-organization-reporting-template-dto.interface';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import * as i18nIsoCountries from 'i18n-iso-countries';
import { cloneDeep } from 'lodash-es';
import { BehaviorSubject, skip } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
    cpmmActivation,
    fcpManagement,
    getAllOrganizations,
    setOrganizationHgbReportingTemplateToDefault,
    setOrganizationReportingTemplateToDefault,
    setOrganizationExcelTemplateToDefault,
    setOrganizationHgbExcelTemplateToDefault,
    updateOrganizationState,
    uploadOrganizationHgbReportingTemplate,
    uploadOrganizationOrReportingTemplate,
    uploadOrganizationOrExcelTemplate,
    uploadOrganizationHgbExcelTemplate,
} from '../../actions/organization.actions';
import { ChangeFCPStateComponent } from '../../components/change-fcpstate/change-fcpstate.component';
import { CreateOrUpdateLicencePackageComponent } from '../../components/licence-package/create-licence-package.component';
import { ShowOrganizationPackagesComponent } from '../../components/show-organization-packages/show-organization-packages.component';
import { UpdateOrganizationModalComponent } from '../../components/update-organization-modal/update-organization-modal.component';
import { UploadOrganizationPdfTemplateComponent } from '../../components/upload-organization-pdf-template/upload-organization-pdf-template.component';
import { localizationKeys } from '../../constants/localizationKeys';
import { CreateOrUpdateLicencePackageDto } from '../../dtos/CreateLicencePackageDto.interface';
import { GetOrganizationDto } from '../../dtos/getOrganizationDto.interface';
import { UsageData } from '../../dtos/usageData.interface';
import { CPMMState } from '../../enums/cpmm-state.enum';
import { OrganizationManagementListMenuActions } from '../../enums/organization-management-list-actions.enum';
import { OrganizationState } from '../../enums/organization-state.enum';
import { FCPState } from '../../enums/subscription-state.enum';
import { OrganizationViewModel } from '../../interfaces/organization-with-country.interface';
import { IUpdateOrganizationDialogData } from '../../interfaces/update-organization-dialog-data.interface';
import { IUpdateOrganizationState } from '../../interfaces/update-organization-state-request.interface';
import { LicenceProvider } from '../../provider/licence.provider';
import { OrganizationProvider } from '../../provider/organisation.provider';

declare const require;
@Component({
    selector: 'aumico-organizations-list',
    templateUrl: './organizations-list.component.html',
    styleUrls: ['./organizations-list.component.scss'],
})
@UntilDestroy()
export class OrganizationsListComponent implements OnInit {
    options: ITableOptions;
    dataLoaded: boolean;
    data: OrganizationViewModel[];
    private language: string;
    @Input() onFilterEvent = new BehaviorSubject<string>(undefined);

    constructor(
        private readonly organizationStoreProvider: OrganizationProvider,
        private readonly licenceProvider: LicenceProvider,
        private readonly dialog: MatDialog,
        private readonly actionListener$: ActionsSubject,
        private readonly coreProvider: AumicoCoreProvider,
    ) {
        this.language = localStorage.getItem('currentLanguage')
            ? localStorage.getItem('currentLanguage').split('-')[0]
            : 'de';
        this.options = {
            columns: [
                { name: 'name', type: 'text', property: 'name' },
                { name: 'country', type: 'text', property: 'country' },
                { name: 'usage (active/inactive)', type: 'text', property: 'usageData' },
                { name: 'closing process subscription', type: 'text', property: 'fcpSubscription' },
                { name: 'closing process start date', type: 'text', property: 'fcpActivationStartDate' },
                { name: 'cpmm activation', type: 'text', property: 'cpmmActivationState' },
                { name: 'state', type: 'text', property: 'state' },
                { name: 'cancellation reason', type: 'text', property: 'surveyData' },
                {
                    name: 'actions',
                },
            ],
            menuPerRow: this.getMenuOptions(),
        };

        this.initializeIsoCountry();
    }

    ngOnInit(): void {
        this.organizationStoreProvider.dispatchGetOrganizations();
        this.organizationStoreProvider
            .getAllOrganizations$()
            .pipe(
                skip(1),
                map((organizations: GetOrganizationDto[]) => {
                    return organizations.map((organization: GetOrganizationDto) =>
                        mapToOrganizationViewModel(organization, this.language, this.dateFormat),
                    );
                }),
                untilDestroyed(this),
            )
            .subscribe((organizations: OrganizationViewModel[]) => {
                this.handleGetAllOrganizations(organizations);
            });

        this.actionListener$
            .pipe(
                ofType(
                    getAllOrganizations.failure,
                    updateOrganizationState.failure,
                    fcpManagement.updateFcpFailure,
                    cpmmActivation.changeCpmmStateFailure,
                ),
                untilDestroyed(this),
            )
            .subscribe(() => this.handleError());
        this.actionListener$
            .pipe(
                ofType(
                    updateOrganizationState.success,
                    fcpManagement.updateFcpSuccess,
                    cpmmActivation.changeCpmmStateSuccess,
                ),
                untilDestroyed(this),
            )
            .subscribe(() => this.handleSuccess());

        this.actionListener$
            .pipe(ofType(uploadOrganizationOrReportingTemplate.failure), untilDestroyed(this))
            .subscribe(() => this.handleErrorInUploadReportingTemplate());
        this.actionListener$
            .pipe(ofType(uploadOrganizationOrReportingTemplate.success), untilDestroyed(this))
            .subscribe(() => this.handleSuccessInUploadReportingTemplate());

        this.actionListener$
            .pipe(ofType(uploadOrganizationHgbReportingTemplate.failure), untilDestroyed(this))
            .subscribe(() => this.handleErrorInUploadReportingTemplate());
        this.actionListener$
            .pipe(ofType(uploadOrganizationHgbReportingTemplate.success), untilDestroyed(this))
            .subscribe(() => this.handleSuccessInUploadReportingTemplate());

        this.actionListener$
            .pipe(ofType(setOrganizationReportingTemplateToDefault.failure), untilDestroyed(this))
            .subscribe(() => this.handleErrorInSetDefaultTemplate());
        this.actionListener$
            .pipe(ofType(setOrganizationReportingTemplateToDefault.success), untilDestroyed(this))
            .subscribe(() => this.handleSuccessInSetDefaultTemplate());

        this.actionListener$
            .pipe(ofType(setOrganizationHgbReportingTemplateToDefault.failure), untilDestroyed(this))
            .subscribe(() => this.handleErrorInSetDefaultTemplate());
        this.actionListener$
            .pipe(ofType(setOrganizationHgbReportingTemplateToDefault.success), untilDestroyed(this))
            .subscribe(() => this.handleSuccessInSetDefaultTemplate());

        this.actionListener$
            .pipe(ofType(uploadOrganizationOrExcelTemplate.failure), untilDestroyed(this))
            .subscribe(() => this.handleErrorInUploadReportingTemplate());
        this.actionListener$
            .pipe(ofType(uploadOrganizationOrExcelTemplate.success), untilDestroyed(this))
            .subscribe(() => this.handleSuccessInUploadReportingTemplate());

        this.actionListener$
            .pipe(ofType(uploadOrganizationHgbExcelTemplate.failure), untilDestroyed(this))
            .subscribe(() => this.handleErrorInUploadReportingTemplate());
        this.actionListener$
            .pipe(ofType(uploadOrganizationHgbExcelTemplate.success), untilDestroyed(this))
            .subscribe(() => this.handleSuccessInUploadReportingTemplate());

        this.actionListener$
            .pipe(ofType(setOrganizationExcelTemplateToDefault.failure), untilDestroyed(this))
            .subscribe(() => this.handleErrorInSetDefaultTemplate());
        this.actionListener$
            .pipe(ofType(setOrganizationExcelTemplateToDefault.success), untilDestroyed(this))
            .subscribe(() => this.handleSuccessInSetDefaultTemplate());

        this.actionListener$
            .pipe(ofType(setOrganizationHgbExcelTemplateToDefault.failure), untilDestroyed(this))
            .subscribe(() => this.handleErrorInSetDefaultTemplate());
        this.actionListener$
            .pipe(ofType(setOrganizationHgbExcelTemplateToDefault.success), untilDestroyed(this))
            .subscribe(() => this.handleSuccessInSetDefaultTemplate());
    }
    onMenuItemPerRowClick(action: string, organization: OrganizationViewModel): void {
        const mappedOrganization: GetOrganizationDto = mapFromDialogOrganization(organization);

        switch (action) {
            case OrganizationManagementListMenuActions.CHANGE_ORGANIZATION_STATE:
                {
                    const stateDialogData: IUpdateOrganizationDialogData = {
                        organization: mappedOrganization,
                        action: (organizationState: OrganizationState) =>
                            this.updateOrganizationState(organizationState, organization),
                    };
                    this.openUserManagementConfirmationDialog(stateDialogData);
                }
                break;
            case OrganizationManagementListMenuActions.UPLOAD_OR_PDF_TEMPLATE:
                const uploadOrReportingTemplateDialogData: IUpdateOrganizationDialogData = {
                    organization: mappedOrganization,
                    action: (reportingTemplateFile) =>
                        this.uploadOrganizationOrReportingTemplate(reportingTemplateFile, organization),
                };
                this.openUploadOrganizationPdfTemplateDialog(uploadOrReportingTemplateDialogData);
                break;
            case OrganizationManagementListMenuActions.UPLOAD_HGB_PDF_TEMPLATE:
                const uploadHgbReportingTemplateDialogData: IUpdateOrganizationDialogData = {
                    organization: mappedOrganization,
                    action: (reportingTemplateFile) =>
                        this.uploadOrganizationHgbReportingTemplate(reportingTemplateFile, organization),
                };
                this.openUploadOrganizationPdfTemplateDialog(uploadHgbReportingTemplateDialogData);
                break;
            case OrganizationManagementListMenuActions.RESET_OR_TO_DEFAULT_TEMPLATE:
                this.resetOrPdfTemplateToDefault(organization);
                break;
            case OrganizationManagementListMenuActions.RESET_HGB_TO_DEFAULT_TEMPLATE:
                this.resetHgbPdfTemplateToDefault(organization);
                break;
            case OrganizationManagementListMenuActions.UPLOAD_OR_EXCEL_TEMPLATE:
                const uploadOrExcelTemplateDialogData: IUpdateOrganizationDialogData = {
                    organization: mappedOrganization,
                    isExcel: true,
                    action: (excelTemplateFile) =>
                        this.uploadOrganizationOrExcelTemplate(excelTemplateFile, organization),
                };
                this.openUploadOrganizationPdfTemplateDialog(uploadOrExcelTemplateDialogData);
                break;
            case OrganizationManagementListMenuActions.UPLOAD_HGB_EXCEL_TEMPLATE:
                const uploadHgbExcelTemplateDialogData: IUpdateOrganizationDialogData = {
                    organization: mappedOrganization,
                    isExcel: true,
                    action: (excelTemplateFile) =>
                        this.uploadOrganizationHgbExcelTemplate(excelTemplateFile, organization),
                };
                this.openUploadOrganizationPdfTemplateDialog(uploadHgbExcelTemplateDialogData);
                break;
            case OrganizationManagementListMenuActions.RESET_OR_TO_DEFAULT_EXCEL_TEMPLATE:
                this.resetOrExcelTemplateToDefault(organization);
                break;
            case OrganizationManagementListMenuActions.RESET_HGB_TO_DEFAULT_EXCEL_TEMPLATE:
                this.resetHgbExcelTemplateToDefault(organization);
                break;

            case OrganizationManagementListMenuActions.CHANGE_FCP_STATE:
                this.openfcpState(organization);

                break;
            case OrganizationManagementListMenuActions.ACTIVATE_CPMM:
                alert(
                    'This action is must be done by the user from the frontend. Its not allowed from the admin gui anymore.',
                );
                break;
            case OrganizationManagementListMenuActions.DEACTIVATE_CPMM:
                this.deactivateCpmm(organization.id);

                break;
            case OrganizationManagementListMenuActions.ADD_PACKAGE:
                this.openNewPackage(organization);

                break;
            case OrganizationManagementListMenuActions.SHOW_PACKAGES:
                this.showListOfPackages(organization);

                break;
        }
    }

    private resetOrPdfTemplateToDefault(organization: OrganizationViewModel): void {
        this.organizationStoreProvider.dispatchSetOrganizationReportingTemplateToDefault(organization.id);
    }

    private resetHgbPdfTemplateToDefault(organization: OrganizationViewModel): void {
        this.organizationStoreProvider.dispatchSetOrganizationHgbReportingTemplateToDefault(organization.id);
    }

    private resetOrExcelTemplateToDefault(organization: OrganizationViewModel): void {
        this.organizationStoreProvider.dispatchSetOrganizationExcelTemplateToDefault(organization.id);
    }

    private resetHgbExcelTemplateToDefault(organization: OrganizationViewModel): void {
        this.organizationStoreProvider.dispatchSetOrganizationHgbExcelTemplateToDefault(organization.id);
    }

    private changeFCPStatus(organizationId: string, fcpState: FCPState): void {
        this.organizationStoreProvider.dispatchChangeFCPStatus(organizationId, fcpState);
    }

    private activateCpmm(organizationId: string): void {
        this.organizationStoreProvider.dispatchChangeCPMMState(organizationId, CPMMState.ACTIVE);
    }

    private deactivateCpmm(organizationId: string): void {
        this.organizationStoreProvider.dispatchChangeCPMMState(organizationId, CPMMState.INACTIVE);
    }

    public updateOrganizationState(state: OrganizationState, organization: OrganizationViewModel) {
        if (organization.state === state) {
            return;
        }
        const updateOrganizationState: IUpdateOrganizationState = {
            id: organization.id,
            state,
        };
        this.organizationStoreProvider.dispatchUpdateOrganizationState(updateOrganizationState);
    }

    public uploadOrganizationOrReportingTemplate(
        reportingTemplateFile: FormData | null,
        organization: OrganizationViewModel,
    ) {
        const uploadOrganizationReportingTemplate: IUploadOrganizationReportingTemplateDto = {
            id: organization.id,
            reportingTemplateFile,
        };
        this.organizationStoreProvider.dispatchUploadOrganizationOrReportingTemplate(
            uploadOrganizationReportingTemplate,
        );
    }

    public uploadOrganizationHgbReportingTemplate(
        reportingTemplateFile: FormData | null,
        organization: OrganizationViewModel,
    ) {
        const uploadOrganizationReportingTemplate: IUploadOrganizationReportingTemplateDto = {
            id: organization.id,
            reportingTemplateFile,
        };
        this.organizationStoreProvider.dispatchUploadOrganizationHgbReportingTemplate(
            uploadOrganizationReportingTemplate,
        );
    }

    public uploadOrganizationOrExcelTemplate(
        excelTemplateFile: FormData | null,
        organization: OrganizationViewModel,
    ) {
        const uploadOrganizationExcelTemplate: IUploadOrganizationReportingTemplateDto = {
            id: organization.id,
            reportingTemplateFile: excelTemplateFile,
        };
        this.organizationStoreProvider.dispatchUploadOrganizationOrExcelTemplate(
            uploadOrganizationExcelTemplate,
        );
    }

    public uploadOrganizationHgbExcelTemplate(
        excelTemplateFile: FormData | null,
        organization: OrganizationViewModel,
    ) {
        const uploadOrganizationExcelTemplate: IUploadOrganizationReportingTemplateDto = {
            id: organization.id,
            reportingTemplateFile: excelTemplateFile,
        };
        this.organizationStoreProvider.dispatchUploadOrganizationHgbExcelTemplate(
            uploadOrganizationExcelTemplate,
        );
    }

    private handleSuccess(): void {
        this.coreProvider.dispatchAddNotification(
            'aumico_admin_user_list_notification_success_title',
            'aumico_admin_user_notification_success_description',
            NotificationType.SUCCESS,
        );
    }
    private handleError(): void {
        this.coreProvider.dispatchAddNotification(
            localizationKeys.aumico_admin_user_list_notification_failure_title,
            localizationKeys.aumico_admin_user_list_notification_failure_description,
            NotificationType.ERROR,
        );
    }

    private handleSuccessInSetDefaultTemplate(): void {
        this.coreProvider.dispatchAddNotification(
            localizationKeys.aumico_admin_user_list_notification_success_title,
            localizationKeys.aumico_users_organization_reset_to_default_template_action_success,
            NotificationType.SUCCESS,
        );
    }
    private handleErrorInSetDefaultTemplate(): void {
        this.coreProvider.dispatchAddNotification(
            localizationKeys.aumico_admin_user_list_notification_failure_title,
            localizationKeys.aumico_users_organization_reset_to_default_template_action_failed,
            NotificationType.ERROR,
        );
    }

    private handleSuccessInUploadReportingTemplate(): void {
        this.coreProvider.dispatchAddNotification(
            localizationKeys.aumico_admin_user_list_notification_success_title,
            localizationKeys.aumico_users_organization_upload_pdf_template_success_notification_description,
            NotificationType.SUCCESS,
        );
    }
    private handleErrorInUploadReportingTemplate(): void {
        this.coreProvider.dispatchAddNotification(
            localizationKeys.aumico_admin_user_list_notification_failure_title,
            localizationKeys.aumico_users_organization_upload_pdf_template_failure_notification_description,
            NotificationType.ERROR,
        );
    }

    private handleGetAllOrganizations(organizations: OrganizationViewModel[]): void {
        if (organizations != null && organizations.length > 0) {
            this.data = [...organizations];
            this.dataLoaded = true;
        }
    }

    private getMenuOptions(): IAumMenuOptionsForRow {
        return {
            items: [
                {
                    interchangeable: false,
                    caption: localizationKeys.aumico_users_organization_change_organization_state_action,
                    icon: 'supervisor_account',
                    action: OrganizationManagementListMenuActions.CHANGE_ORGANIZATION_STATE,
                },
                {
                    interchangeable: false,
                    caption: localizationKeys.aumico_users_organization_upload_or_pdf_template_action,
                    icon: 'picture_as_pdf',
                    action: OrganizationManagementListMenuActions.UPLOAD_OR_PDF_TEMPLATE,
                },
                {
                    interchangeable: false,
                    caption: localizationKeys.aumico_users_organization_upload_hgb_pdf_template_action,
                    icon: 'picture_as_pdf',
                    action: OrganizationManagementListMenuActions.UPLOAD_HGB_PDF_TEMPLATE,
                },
                {
                    interchangeable: false,
                    caption: localizationKeys.aumico_users_organization_reset_to_default_or_template_action,
                    icon: 'picture_as_pdf',
                    action: OrganizationManagementListMenuActions.RESET_OR_TO_DEFAULT_TEMPLATE,
                },
                {
                    interchangeable: false,
                    caption: localizationKeys.aumico_users_organization_reset_to_default_hgb_template_action,
                    icon: 'picture_as_pdf',
                    action: OrganizationManagementListMenuActions.RESET_HGB_TO_DEFAULT_TEMPLATE,
                },
                {
                    interchangeable: false,
                    caption: 'Upload OR Excel Template',
                    icon: 'table_chart',
                    action: OrganizationManagementListMenuActions.UPLOAD_OR_EXCEL_TEMPLATE,
                },
                {
                    interchangeable: false,
                    caption: 'Upload HGB Excel Template',
                    icon: 'table_chart',
                    action: OrganizationManagementListMenuActions.UPLOAD_HGB_EXCEL_TEMPLATE,
                },
                {
                    interchangeable: false,
                    caption: 'Reset OR Excel Template to Default',
                    icon: 'table_chart',
                    action: OrganizationManagementListMenuActions.RESET_OR_TO_DEFAULT_EXCEL_TEMPLATE,
                },
                {
                    interchangeable: false,
                    caption: 'Reset HGB Excel Template to Default',
                    icon: 'table_chart',
                    action: OrganizationManagementListMenuActions.RESET_HGB_TO_DEFAULT_EXCEL_TEMPLATE,
                },
                {
                    interchangeable: false,
                    caption: 'aumico_admin_menu_item_change_fcp',
                    action: OrganizationManagementListMenuActions.CHANGE_FCP_STATE,
                    icon: 'subscriptions',
                },
                {
                    interchangeable: true,
                    conditionalOptions: [
                        {
                            propertyKey: 'cpmmActivationState',
                            value: CPMMState.ACTIVE,
                            caption: 'aumico_admin_menu_item_deactivate_cpmm',
                            icon: 'lock_open',
                            action: OrganizationManagementListMenuActions.DEACTIVATE_CPMM,
                        },
                        {
                            propertyKey: 'cpmmActivationState',
                            value: CPMMState.INACTIVE,
                            caption: 'aumico_admin_menu_item_activate_cpmm',
                            icon: 'lock',
                            disabled: true,
                            action: OrganizationManagementListMenuActions.ACTIVATE_CPMM,
                        },
                        {
                            propertyKey: 'cpmmActivationState',
                            value: '',
                            caption: 'aumico_admin_menu_item_activate_cpmm',
                            icon: 'lock',
                            disabled: true,
                            action: OrganizationManagementListMenuActions.ACTIVATE_CPMM,
                        },
                    ],
                },
                {
                    interchangeable: false,
                    caption: 'add package',
                    action: OrganizationManagementListMenuActions.ADD_PACKAGE,
                    icon: 'subscriptions',
                },
                {
                    interchangeable: false,
                    caption: 'show packages',
                    action: OrganizationManagementListMenuActions.SHOW_PACKAGES,
                    icon: 'subscriptions',
                },
            ],
        };
    }
    private openUserManagementConfirmationDialog(data: IUpdateOrganizationDialogData): void {
        this.dialog.open(UpdateOrganizationModalComponent, { data });
    }

    private openfcpState(organization: OrganizationViewModel): void {
        const dialogRef = this.dialog.open(ChangeFCPStateComponent, {
            data: { fcpStatus: organization.fcpSubscription },
        });
        dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe((fcpState: FCPState) => {
                if (fcpState != null) {
                    this.changeFCPStatus(organization.id, fcpState);
                }
            });
    }

    /**
     * Opens the dialog to create a new licence package.
     * @param organization - The organization for which the licence package is being created.
     */
    private openNewPackage(organization: OrganizationViewModel): void {
        const dialogRef = this.dialog.open(CreateOrUpdateLicencePackageComponent, {
            width: '600px', // Adjust the width as needed
            data: { organizationId: organization.id },
        });

        dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe((createLicencePackageDto: CreateOrUpdateLicencePackageDto | undefined) => {
                if (createLicencePackageDto) {
                    // Ensure organizationId is present
                    if (!createLicencePackageDto.organizationId) {
                        createLicencePackageDto.organizationId = organization.id;
                    }

                    this.licenceProvider.dispatchAddPackage(organization.id, createLicencePackageDto);
                }
            });
    }

    private showListOfPackages(organization: OrganizationViewModel): void {
        this.dialog.open(ShowOrganizationPackagesComponent, {
            width: '600px', // Adjust the width as needed
            data: {
                packages: cloneDeep(organization.licenceData),
                organizationId: organization.id,
                organizationName: organization.name,
            },
        });
    }

    private openUploadOrganizationPdfTemplateDialog(data: IUpdateOrganizationDialogData): void {
        this.dialog.open(UploadOrganizationPdfTemplateComponent, { data });
    }

    private initializeIsoCountry(): void {
        i18nIsoCountries.registerLocale(require('i18n-iso-countries/langs/en.json'));
        i18nIsoCountries.registerLocale(require('i18n-iso-countries/langs/de.json'));
    }

    private dateFormat(dateToFormat: Date | undefined): string {
        if (dateToFormat == null) return '-';

        const dateOptions: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
        const date = new Date(dateToFormat);

        const dateTimeFormat = new Intl.DateTimeFormat('de-CH', dateOptions);
        return dateTimeFormat.format(date);
    }
}

/**
 * Maps a GetOrganizationDto to OrganizationViewModel by formatting usageData.
 * @param organization The original organization object.
 * @param language The current language for country name localization.
 * @returns The mapped OrganizationViewModel object.
 */
function mapToOrganizationViewModel(
    organization: GetOrganizationDto,
    language: string,
    dateFormat: (date: Date | undefined) => string,
): OrganizationViewModel {
    const formattedUsageData = organization.usageData
        ? `${organization.usageData.activeCompanies}/${organization.usageData.inactiveCompanies}`
        : '';

    const { fcpActivationDetails, cpmmActivationDetails } = organization;

    return {
        ...organization,
        country: organization.countryISO31661Alpha2Code
            ? i18nIsoCountries.getName(organization.countryISO31661Alpha2Code, language)
            : organization.countryISO31661Alpha2Code,

        fcpActivationStartDate: dateFormat(fcpActivationDetails?.activationStartDate),
        fcpSubscription: fcpActivationDetails?.subscriptionState || '',
        cpmmActivationState: cpmmActivationDetails?.activationState || '',
        surveyData:
            fcpActivationDetails?.subscriptionState === FCPState.CANCELLED ? fcpActivationDetails?.surveyData : '',
        usageData: formattedUsageData,
    } as OrganizationViewModel;
}

/**
 * Maps a OrganizationViewModel back to GetOrganizationDto by parsing usageData.
 * @param mappedOrganization The mapped organization object with formatted usageData.
 * @returns The original organization object with usageData as an object.
 */
function mapFromDialogOrganization(mappedOrganization: OrganizationViewModel): GetOrganizationDto {
    const [activeStr, inactiveStr] = mappedOrganization.usageData.split('/');
    const activeCompanies = parseInt(activeStr, 10);
    const inactiveCompanies = parseInt(inactiveStr, 10);

    const usageData: UsageData | null =
        activeStr && inactiveStr
            ? {
                  activeCompanies: isNaN(activeCompanies) ? 0 : activeCompanies,
                  inactiveCompanies: isNaN(inactiveCompanies) ? 0 : inactiveCompanies,
              }
            : null;

    const { usageData: _, ...rest } = mappedOrganization;

    return {
        ...rest,
        usageData,
    };
}
